import { useCallback, useLayoutEffect, useState } from 'react';

export interface ScrollOverflowEdges {
    /** Content is hidden past the start (left) edge. */
    start: boolean;
    /** Content is hidden past the end (right) edge. */
    end: boolean;
}

const NO_OVERFLOW: ScrollOverflowEdges = { start: false, end: false };

// Sub-pixel scroll positions can leave scrollLeft a fraction short of the end.
const EDGE_TOLERANCE_PX = 1;

/** Whether `element` has content hidden past either horizontal edge right now. */
export const measureScrollOverflowEdges = (element: HTMLElement): ScrollOverflowEdges => {
    const maxScrollLeft = element.scrollWidth - element.clientWidth;
    return {
        start: element.scrollLeft > EDGE_TOLERANCE_PX,
        end: maxScrollLeft - element.scrollLeft > EDGE_TOLERANCE_PX,
    };
};

/**
 * Reports whether a horizontally scrolling element has content hidden past
 * either edge, so the caller can hint that there is more to scroll.
 *
 * Re-measures after every render (the content may have changed), when the
 * element scrolls, and when it is resized.
 */
export const useScrollOverflowEdges = (
    ref: React.RefObject<HTMLElement | null>,
): ScrollOverflowEdges => {
    const [edges, setEdges] = useState<ScrollOverflowEdges>(NO_OVERFLOW);

    const update = useCallback(() => {
        const element = ref.current;
        if (!element) {
            return;
        }
        const next = measureScrollOverflowEdges(element);
        setEdges((previous) =>
            previous.start === next.start && previous.end === next.end ? previous : next,
        );
    }, [ref]);

    useLayoutEffect(update);

    useLayoutEffect(() => {
        const element = ref.current;
        if (!element) {
            return;
        }
        element.addEventListener('scroll', update, { passive: true });
        const resizeObserver = new ResizeObserver(update);
        resizeObserver.observe(element);
        return () => {
            element.removeEventListener('scroll', update);
            resizeObserver.disconnect();
        };
    }, [ref, update]);

    return edges;
};
